import { Alert, Button, FormControlLabel, Grid, Switch, TextField, Typography, styled, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import AppTextField from 'components/input-fields/AppTextField';
import { H2, H4 } from 'components/Typography';
import { useFormik } from 'formik';
import { useState, type FC, useEffect } from 'react';
import * as Yup from 'yup';
import CalendarInput from 'components/input-fields/CalendarInput';
import { AgreementEntity, ContractEntityLegalContractTypeEnum, CreateAgreementDto, UpdateAgreementDto } from 'api/generated';
import { useTranslation } from 'react-i18next';
import { isEndDateValid, isValidDate } from 'utils/dateValidator';
import { ContractFormData } from 'page-sections/employees/Contract';
import { addDays, differenceInDays, isSameDay } from 'date-fns';
import AppTextAreaAutosize from 'components/input-fields/AppTextAreaAutosize';
import { agreementApi, tenantConfigApi } from 'api';
import { decryptData, encryptData } from 'utils/encryptionDecryptionAgreements';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import toast, { Toaster } from 'react-hot-toast';
import { wait } from '@testing-library/user-event/dist/utils';
import DaysOfWeekPicker, { DaysOfTheWeek } from 'components/input-fields/DaysOfWeekPicker';

export type CreateAgreementDtoNoId = Omit<CreateAgreementDto, 'employeeId'>;


// component props interface
interface ModalProps {
    contractData?: ContractFormData;
    contractId: number | null;
    data?: AddAgreementFormData | null;
    agreements: AgreementEntity[];
    id?: number;
    open: boolean;
    edit?: boolean;
    onClose: () => void;
    onSubmit: (data: CreateAgreementDtoNoId) => Promise<any>;
}
export interface AddAgreementFormData {
    net_daily: number;
    startedOn: Date;
    agreementIndex?: number;
    endedOn?: Date;
    downpayment?: number;
    notes?: string;
    freeDays: object[],
    isLongTheWholeContract: boolean,
    id?: number;
    net_daily_encrypted?: string;
    createDaysAutomatically: boolean;
    daysOff: number;
    hoursVacation?: number;
    daysVacation?: number;
    weeklyWorkHours?: number;
    balance730?: number;
    balance730_encrypted?: string;
    balance730_date?: Date;
    costPerHour?: number;
    costPerHour_encrypted?: string;
}
// styled components
const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 900,
    minWidth: 325,
    maxHeight: '80vh',
    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const AddAgreememtModal: FC<ModalProps> = ({ open, onClose, edit, contractData, data, id, onSubmit, contractId, agreements }) => {
    const { t } = useTranslation();
    const { encryptionKey } = useEncryption();
    const [startDate, setStartDate] = useState<Date | null | undefined>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [netDaily, setNetDaily] = useState(data?.net_daily_encrypted ? Number(decryptData(data?.net_daily_encrypted, encryptionKey)) : data?.net_daily ?? 0);
    const [netMonthly, setNetMonthly] = useState(0);
    const [totalDaysOff, setTotalDaysOff] = useState(0);
    const [netTotalContract, setNetTotalContract] = useState(0);
    const [netYearly, setNetYearly] = useState(0);
    const [isUnlimited, setIsUnlimited] = useState(contractData?.legalContractType === ContractEntityLegalContractTypeEnum.Unlimited);
    const [isFirstAgreement, setIsFirstAgreement] = useState(true);
    const [isLastAgreement, setIsLastAgreement] = useState(true);
    const [vacationConfig, setVacationConfig] = useState<{
        vacatioinInHours: boolean;
        defaultHoursVacation: number;
        defaultDaysVacation: number;
    } | null>(null);
    const [hoursVacation, setHoursVacation] = useState<number>(0);
    const [daysVacation, setDaysVacation] = useState<number>(0);
    const [vacationType, setVacationType] = useState<'hours' | 'days'>('days');
    // Store the last entered values to preserve them when switching
    const [lastEnteredHours, setLastEnteredHours] = useState<number>(0);
    const [lastEnteredDays, setLastEnteredDays] = useState<number>(0);
    // 730 Balance fields
    const [balance730, setBalance730] = useState<number>(0);
    const [balance730Input, setBalance730Input] = useState<string>('0');
    const [balance730Date, setBalance730Date] = useState<Date | null>(null);
    // Weekly Work Hours field
    const [weeklyWorkHours, setWeeklyWorkHours] = useState<number>(data?.weeklyWorkHours ?? 40);
    // Cost-field role gating per Epic 6.6 spec §4.3
    const userRole = (() => {
      try {
        const tok = localStorage.getItem('accessToken');
        if (!tok) return '';
        const payload = JSON.parse(atob(tok.split('.')[1]));
        return payload.role ?? '';
      } catch { return ''; }
    })();
    const canEditCost = userRole === 'OWNER' || userRole === 'CONTROLLER';
    const canViewCost = canEditCost || userRole === 'MANAGER';
    // Costo Orario Aziendale (€/h) — fully-loaded employer cost. Empty/null = not tracked.
    const initialCostPerHour: number | '' = (() => {
      if ((data as any)?.costPerHour_encrypted) {
        const dec = decryptData((data as any).costPerHour_encrypted, encryptionKey);
        const n = Number(dec);
        return isFinite(n) ? n : '';
      }
      const plain = (data as any)?.costPerHour;
      return typeof plain === 'number' ? plain : '';
    })();
    const [costPerHour, setCostPerHour] = useState<number | ''>(initialCostPerHour);

    const initialValues: AddAgreementFormData = {
        net_daily: data?.net_daily_encrypted ? Number(decryptData(data?.net_daily_encrypted, encryptionKey)) : data?.net_daily ?? 0,
        isLongTheWholeContract: data?.isLongTheWholeContract ?? true,
        startedOn: new Date(data?.startedOn ?? contractData?.endedOn ?? new Date()),
        endedOn: data?.endedOn ? new Date(data?.endedOn) : contractData?.endedOn ? new Date(contractData?.endedOn) : undefined,
        downpayment: data?.downpayment ?? 0,
        notes: data?.notes ?? "",
        freeDays: data?.freeDays ?? [],
        createDaysAutomatically: data?.createDaysAutomatically ?? true,
        daysOff: data?.daysOff ?? 0,
        hoursVacation: data?.hoursVacation ?? 0,
        daysVacation: data?.daysVacation ?? 0,
        weeklyWorkHours: data?.weeklyWorkHours ?? 40,
        balance730: data?.balance730_encrypted ? Number(decryptData(data?.balance730_encrypted, encryptionKey)) : data?.balance730 ?? 0,
        balance730_date: data?.balance730_date ? new Date(data?.balance730_date) : undefined,
        costPerHour: typeof initialCostPerHour === 'number' ? initialCostPerHour : undefined,
    };

    const fieldValidationSchema = Yup.object().shape({
        net_daily: Yup.number().required(t('common.forms.field.required', {
            field: t('employees.agreements.field.net'),
        })),
        isLongTheWholeContract: Yup.boolean(),
        startedOn: Yup.date()
            .typeError(t('employees.agreements.validation.typeError'))
            .required(t('common.forms.field.required', {
                field: t('employees.agreements.field.date'),
            }))
            .test('is-valid-date', 'Invalid date', (value) => {
                return isValidDate(value);
            })
            .test('isStartDateValid', t('employees.agreements.validation.startDateTime'), function (value) {
                if (value && this.parent.endedOn && isValidDate(this.parent.endedOn)) {
                    return +value <= this.parent.endedOn;
                }
                return true;
            }),
        endedOn: Yup.date()
            .notRequired()
            .test('isValidDate', 'Invalid date', (value) => {
                if (value && !isUnlimited) {
                    return isValidDate(value);
                }
                return true;
            })
            .test('isEndDateValid', t('employees.agreements.validation.endDateTime'), function (value) {
                if (value && !isUnlimited) {
                    return isEndDateValid(value, this.parent.startedOn);
                }
                return true;
            }),
        downpayment: Yup.number().notRequired(),
        notes: Yup.string().notRequired(),
        createDaysAutomatically: Yup.boolean(),
        daysOff: Yup.number().when('createDaysAutomatically', {
            is: false,
            then: (schema) => schema.required().min(0)
        }),
    });

    const findAgreementPosition = () => {
        if (!data) {
            setIsLastAgreement(false);
            setIsFirstAgreement(false);
            return;
        }
        setIsLastAgreement(agreements.findIndex(value => value.id === data.id) === agreements.length - 1);
        setIsFirstAgreement(agreements.findIndex(value => value.id === data.id) === 0);
    }

    const fetchVacationConfig = async () => {
        try {
            const response = await tenantConfigApi.getVacationConfig();
            if (response.data) {
                setVacationConfig(response.data);
                // Only set defaults for new agreements (when data is null)
                if (!data) {
                    if (response.data.vacatioinInHours) {
                        setVacationType('hours');
                        setHoursVacation(response.data.defaultHoursVacation);
                    } else {
                        setVacationType('days');
                        setDaysVacation(response.data.defaultDaysVacation);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching vacation config:', error);
        }
    };

    const handleClose = () => {
        formik.resetForm();
        resetModalData();
        onClose();
    }

    const formik = useFormik({
        initialValues,
        validationSchema: fieldValidationSchema,
        onSubmit: (values) => {
            const validEndDate = endDate instanceof Date && !isNaN(endDate.getTime());
            if (contractId) {
                const costPerHourPlain = encryptionKey ? undefined : (costPerHour === '' ? undefined : costPerHour);
                const costPerHourEnc = (encryptionKey && costPerHour !== '') ? encryptData(costPerHour, encryptionKey) : undefined;
                const updateValues: CreateAgreementDto = {
                    ...values,
                    net_daily: encryptionKey ? undefined : values.net_daily,
                    net_daily_encrypted: encryptionKey ? encryptData(values.net_daily, encryptionKey) : undefined,
                    startedOn: startDate?.toISOString() ?? new Date().toISOString(),
                    endedOn: validEndDate ? endDate.toISOString() : undefined,
                    contractId,
                    hoursVacation: vacationType === 'hours' ? hoursVacation : undefined,
                    daysVacation: vacationType === 'days' ? daysVacation : undefined,
                    weeklyWorkHours: values.weeklyWorkHours ?? weeklyWorkHours ?? 40,
                    balance730: encryptionKey ? undefined : balance730,
                    balance730_encrypted: encryptionKey ? encryptData(balance730, encryptionKey) : undefined,
                    balance730_date: balance730Date?.toISOString(),
                    costPerHour: costPerHourPlain,
                    costPerHour_encrypted: costPerHourEnc,
                };


                if (!edit) {
                    if (updateValues.contractId !== null) {
                        onSubmit(updateValues)
                            .then(() => {
                                handleClose();
                            }).catch((e) => {
                                if (e.response.status === 400) {
                                    toast.error(t('employees.agreements.validation.outOfBound'));
                                }
                            })
                    }
                } else if (id) {
                    const updatedData: UpdateAgreementDto = {
                        ...values,
                        net_daily: encryptionKey ? undefined : values.net_daily,
                        net_daily_encrypted: encryptionKey ? encryptData(values.net_daily, encryptionKey) : undefined,
                        startedOn: values.startedOn.toString(),
                        endedOn: values.endedOn?.toString(),
                        hoursVacation: vacationType === 'hours' ? hoursVacation : undefined,
                        daysVacation: vacationType === 'days' ? daysVacation : undefined,
                        weeklyWorkHours: values.weeklyWorkHours ?? weeklyWorkHours ?? 40,
                        balance730: encryptionKey ? undefined : balance730,
                        balance730_encrypted: encryptionKey ? encryptData(balance730, encryptionKey) : undefined,
                        balance730_date: balance730Date?.toISOString(),
                        costPerHour: encryptionKey ? undefined : (costPerHour === '' ? undefined : costPerHour),
                        costPerHour_encrypted: (encryptionKey && costPerHour !== '') ? encryptData(costPerHour, encryptionKey) : undefined,
                    }

                    agreementApi.update(id.toString(), updatedData).then(() => {
                        handleClose();
                    }).catch((e) => {
                        if (e.response.status === 400) {
                            if (e.response.data.message === 'overlap')
                                toast.error(t('employees.agreements.validation.overlap'))
                            else
                                toast.error(t('employees.agreements.validation.outOfBound'));
                        }
                    })
                }
            }
        }
    });


    const resetModalData = () => {
        setNetDaily(0);
        setNetMonthly(0);
        setNetYearly(0);
        setNetTotalContract(0);
        setStartDate(null);
        setEndDate(null);
        setHoursVacation(0);
        setDaysVacation(0);
        setVacationType('days');
        setLastEnteredHours(0);
        setLastEnteredDays(0);
        setBalance730(0);
        setBalance730Input('0');
        setBalance730Date(null);
        setWeeklyWorkHours(40);
    }



    /* const handleAgreementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
         values.isLongTheWholeContract = event.target.checked;
         setFieldValue('net_daily', netDaily, true)
     } */


    function isValidDateLocal(dateString: Date | string | null | undefined): boolean {

        if (dateString === null || dateString === undefined) {
            return false;
        }
        if (dateString instanceof Date) {
            return true;
        }
        const parsedDate = Date.parse(dateString);
        return !isNaN(parsedDate);
    }

    useEffect(() => {
        if (contractData) {
            formik.setFieldValue('startedOn', agreements.length > 0 ? undefined : contractData.startedOn, true)
            setStartDate(null);
            const endDate = agreements.length > 1 ? addDays(new Date(agreements[1].startedOn), -1) : contractData.endedOn ? new Date(contractData.endedOn) : null;
            setEndDate(endDate);
            formik.setFieldValue('endedOn', endDate, true)
            setIsUnlimited(contractData?.legalContractType === ContractEntityLegalContractTypeEnum.Unlimited)
        }
        // Initialize weeklyWorkHours to default if creating new agreement (no data)
        if (!data) {
            setWeeklyWorkHours(40);
            formik.setFieldValue('weeklyWorkHours', 40, true);
        }
        if (data) {
            formik.setFieldValue('startedOn', data.startedOn, true);
            formik.setFieldValue('endedOn', data.endedOn, true);
            formik.setFieldValue('freeDays', data.freeDays, true);
            formik.setFieldValue('daysOff', data.daysOff, true);
            formik.setFieldValue('createDaysAutomatically', data.createDaysAutomatically, true);
            formik.setFieldValue('downpayment', data.downpayment, true);
            formik.setFieldValue('notes', data.notes, true);
            formik.setFieldValue('endedOn', data.endedOn, true);
            formik.setFieldValue('net_daily', data?.net_daily_encrypted ? Number(decryptData(data?.net_daily_encrypted, encryptionKey)) : data?.net_daily, true);
            setStartDate(data.startedOn ? new Date(data.startedOn) : null);
            setEndDate(data.endedOn ? new Date(data.endedOn) : null);
            setNetDaily(data?.net_daily_encrypted ? Number(decryptData(data?.net_daily_encrypted, encryptionKey)) : data?.net_daily ?? 0);
            const balanceValue = data?.balance730_encrypted ? Number(decryptData(data?.balance730_encrypted, encryptionKey)) : data?.balance730 ?? 0;
            setBalance730(balanceValue);
            setBalance730Input(balanceValue.toString());
            setBalance730Date(data?.balance730_date ? new Date(data?.balance730_date) : null);
            setWeeklyWorkHours(data?.weeklyWorkHours ?? 40);
            formik.setFieldValue('weeklyWorkHours', data?.weeklyWorkHours ?? 40, true);
            updateNetMonthlyAndTotalContract();
        }
        findAgreementPosition();
        formik.validateForm();
        fetchVacationConfig();

    }, [contractData, open]);

    useEffect(() => {
        updateNetMonthlyAndTotalContract();
    }, [netDaily]);

    // Handle vacation data after vacationConfig is loaded
    useEffect(() => {
        if (data && vacationConfig) {
            // Handle vacation data for editing
            if (data.hoursVacation && data.hoursVacation > 0) {
                // Only hours set - show hours with hours indicator
                setVacationType('hours');
                setHoursVacation(data.hoursVacation);
                setLastEnteredHours(data.hoursVacation); // Store for switching
                setDaysVacation(0);
                formik.setFieldValue('hoursVacation', data.hoursVacation, true);
                formik.setFieldValue('daysVacation', 0, true);
            } else if (data.daysVacation && data.daysVacation > 0) {
                // Only days set - show days with days indicator
                setVacationType('days');
                setDaysVacation(data.daysVacation);
                setLastEnteredDays(data.daysVacation); // Store for switching
                setHoursVacation(0);
                formik.setFieldValue('daysVacation', data.daysVacation, true);
                formik.setFieldValue('hoursVacation', 0, true);
            } else {
                // No vacation data set - show tenant defaults
                if (vacationConfig.vacatioinInHours) {
                    setVacationType('hours');
                    setHoursVacation(vacationConfig.defaultHoursVacation);
                    setDaysVacation(0);
                    formik.setFieldValue('hoursVacation', vacationConfig.defaultHoursVacation, true);
                    formik.setFieldValue('daysVacation', 0, true);
                } else {
                    setVacationType('days');
                    setDaysVacation(vacationConfig.defaultDaysVacation);
                    setHoursVacation(0);
                    formik.setFieldValue('daysVacation', vacationConfig.defaultDaysVacation, true);
                    formik.setFieldValue('hoursVacation', 0, true);
                }
            }
        }
    }, [data, vacationConfig]);

    const updateNetMonthlyAndTotalContract = () => {
        if (isValidDateLocal(formik.values.startedOn)) {
            if (!isUnlimited && isValidDateLocal(formik.values.endedOn)) {
                const daysDifference = differenceInDays(new Date(formik.values.endedOn ?? new Date()), new Date(formik.values.startedOn ?? new Date())) + 1;
                const newNetTotalContract = parseFloat((netDaily * daysDifference).toFixed(4));
                setNetTotalContract(newNetTotalContract);
            } else if (isUnlimited) {
                setNetYearly(parseFloat((netDaily * 420).toFixed(4)));
            }
            setNetMonthly(parseFloat((netDaily * 30).toFixed(4)));
        }
    };
    const calculateTotalDaysOff = (totalDays: number, freeDaysPerWeek: number) => {
        const weeks = totalDays / 7;
        let totalDaysOff = weeks * freeDaysPerWeek;

        // Round to nearest quarter
        totalDaysOff = Math.round(totalDaysOff * 4) / 4;

        return totalDaysOff;
    };
    const integerPart = Math.floor(totalDaysOff);
    const fractionPart = totalDaysOff - integerPart;

    const fractionDisplay = (fraction: number) => {
        if (fraction === 0.25) return '¼';
        if (fraction === 0.5) return '½';
        if (fraction === 0.75) return '¾';
        return '';
    };

    useEffect(() => {
        if (isValidDateLocal(formik.values.startedOn) && isValidDateLocal(formik.values.endedOn)) {
            const days = differenceInDays(new Date(formik.values.endedOn ?? new Date()), new Date(formik.values.startedOn ?? new Date())) + 1;
            setTotalDaysOff(
                calculateTotalDaysOff(
                    days,
                    formik.values.freeDays.filter((elem, index, self) => {
                        return index === self.indexOf(elem);
                    }).length
                )
            );
        }
    }, [
        formik.values.startedOn,
        formik.values.endedOn,
        formik.values.freeDays.filter((elem, index, self) => {
            return index === self.indexOf(elem);
        }).length]
    );

    const totalDays = () => {
        const days = differenceInDays(new Date(formik.values.endedOn ?? new Date()), new Date(formik.values.startedOn ?? new Date())) + 1;
        return days > 0 ? days : '-';
    }



    return (
        <StyledAppModal open={open} handleClose={() => {
            handleClose();
        }}
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
            <Toaster
                position="top-center"
                reverseOrder={false}
            />
            <H2 mb={2}>
                {edit ? t('common.tables.button.editWithItem', { item: t('employees.agreements.name') })
                    : t('common.tables.button.addItem', { item: t('employees.agreements.name'), })}
            </H2>


            <form onSubmit={formik.handleSubmit}>
                <Grid container alignContent={'center'} spacing={2}>

                    {(agreements.length > 0 &&
                        <Grid item container spacing={2} marginTop={0} >

                            <Grid item xs={6}>
                                <H4 mb={1}>{t('employees.agreements.field.startDate')}</H4>
                                <CalendarInput
                                    format='dd.MM.yyyy'
                                    value={startDate}
                                    disabled={isFirstAgreement}
                                    onChange={async (newValue) => {
                                        if (newValue) {
                                            if (!(newValue instanceof Date) || isNaN(newValue.getTime())) {
                                                setStartDate(newValue);
                                                return;
                                            }
                                            const convertedNewValue = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()));
                                            if (contractData?.startedOn && +convertedNewValue < +new Date(contractData.startedOn)) {
                                                /*
                                                    To reset calendar picker value it is needed to set another valid date and then 
                                                    to wait to set calendar to null
                                                */
                                                toast.error(t('employees.agreements.validation.previousDate'));
                                                setStartDate(convertedNewValue)
                                                await wait(1)
                                                setStartDate(data ? new Date(data.startedOn) : null);
                                                return;
                                            }



                                            if (contractData?.endedOn && +convertedNewValue > +new Date(contractData.endedOn)) {
                                                /*
                                                    To reset calendar picker value it is needed to set another valid date and then 
                                                    to wait to set calendar to null
                                                */
                                                toast.error(t('employees.agreements.validation.lateDate'));
                                                setStartDate(convertedNewValue)
                                                await wait(1)
                                                setStartDate(data ? new Date(data.startedOn) : null);
                                                return;
                                            }

                                            if (!convertedNewValue)
                                                return;

                                            setStartDate(convertedNewValue);
                                            formik.setFieldValue('startedOn', convertedNewValue.toUTCString(), true);
                                            if (!data) {
                                                const nextAgreementIndex = agreements.findIndex(value => + convertedNewValue <= +new Date(value.startedOn));
                                                if (nextAgreementIndex !== -1) {
                                                    const agreementDate = new Date(agreements[nextAgreementIndex].startedOn);
                                                    if (isSameDay(agreementDate, convertedNewValue)) {
                                                        toast.error(t('employees.agreements.validation.agreementOnThisDate'));
                                                        /*
                                                            To reset calendar picker value it is needed to set another valid date and then 
                                                            to wait to set calendar to null
                                                        */
                                                        setStartDate(convertedNewValue)
                                                        await wait(1)
                                                        setStartDate(null);
                                                        formik.setFieldValue('startedOn', undefined, true);
                                                        return;
                                                    }
                                                    const endDate = addDays(new Date(agreements[nextAgreementIndex].startedOn), -1);
                                                    setEndDate(endDate);
                                                    formik.setFieldValue('endedOn', endDate.toUTCString(), true);
                                                }
                                                else {
                                                    const endDate = contractData?.endedOn ? new Date(contractData?.endedOn) : null;
                                                    setEndDate(endDate);
                                                    formik.setFieldValue('endedOn', endDate?.toUTCString(), true);
                                                }
                                            }


                                        }

                                        else {
                                            setStartDate(null);
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            error: Boolean(formik.errors.startedOn && formik.touched.startedOn),
                                            helperText: (formik.errors.startedOn && formik.touched.startedOn) ? t('employees.agreements.validation.startDateTime') : undefined,
                                        },

                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <H4 mb={1}>{t('employees.agreements.field.endDate')}</H4>
                                <CalendarInput
                                    format='dd.MM.yyyy'
                                    value={endDate}
                                    // To set end date agreement must be defined and not be the last agreement
                                    disabled={isLastAgreement || !data}
                                    onChange={async (newValue) => {
                                        if (newValue) {
                                            if (!(newValue instanceof Date) || isNaN(newValue.getTime())) {
                                                setStartDate(newValue);
                                                return;
                                            }
                                            const convertedNewValue = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()));

                                            if (contractData?.startedOn && +convertedNewValue < +new Date(contractData.startedOn)) {
                                                toast.error(t('employees.agreements.validation.previousDate'));
                                                /*
                                                    To reset calendar picker value it is needed to set another valid date and then 
                                                    to wait to set calendar to null
                                                */
                                                setEndDate(newValue)
                                                await wait(1)
                                                setEndDate(data?.endedOn ? new Date(data.endedOn) : null);

                                                return;
                                            }

                                            if (contractData?.endedOn && +convertedNewValue > +new Date(contractData.endedOn)) {
                                                toast.error(t('employees.agreements.validation.lateDate'));
                                                /*
                                                    To reset calendar picker value it is needed to set another valid date and then 
                                                    to wait to set calendar to null
                                                */
                                                setEndDate(newValue)
                                                await wait(1)
                                                setEndDate(data?.endedOn ? new Date(data.endedOn) : null);
                                                return;
                                            }

                                            setEndDate(newValue);
                                            formik.setFieldValue('endedOn', newValue.toISOString(), true);
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            error: Boolean(formik.errors.endedOn && formik.touched.endedOn),
                                            helperText: formik.errors.endedOn && formik.touched.endedOn ? t('employees.agreements.validation.endDateTime') : undefined
                                        }
                                    }}
                                />
                            </Grid>


                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <H4 mb={2}>
                            {t("employees.agreements.pay")}
                        </H4>
                    </Grid>
                    <Grid item xs={(isUnlimited ? 4 : 3)}>
                        <AppTextField
                            fullWidth
                            disabled={!(isValidDateLocal(formik.values.startedOn) && (isValidDateLocal(formik.values.endedOn) || isUnlimited))}
                            size='small'
                            name='net_daily'
                            label={t('employees.agreements.field.net')}
                            type='number'
                            inputProps={{ step: 0.01 }}
                            value={netDaily}
                            onChange={(event) => {
                                setNetDaily(parseFloat(event.target.value));
                            }}
                            onBlur={() => {
                                formik.setFieldValue('net_daily', netDaily, true);
                                updateNetMonthlyAndTotalContract();
                            }}
                            error={Boolean(formik.errors.net_daily && formik.touched.net_daily)}
                            helperText={(formik.touched.net_daily && formik.errors.net_daily) as string}

                        />
                    </Grid>
                    <Grid item xs={isUnlimited ? 4 : 3}>
                        <AppTextField
                            fullWidth
                            size='small'
                            name='netmonthly'
                            label={t('employees.agreements.field.monthly.net')}
                            type='number'
                            inputProps={{ step: 0.01 }}
                            value={netMonthly}
                            onChange={(event) => {
                                setNetMonthly(parseFloat(event.target.value));
                            }}
                            onBlur={() => {
                                const newNetDaily = parseFloat((netMonthly / 30).toFixed(4));
                                setNetDaily(newNetDaily);
                                formik.setFieldValue('net_daily', newNetDaily, true);
                            }}
                        />
                    </Grid>
                    {(isUnlimited &&

                        <Grid item xs={isUnlimited ? 4 : 3}>
                            <AppTextField
                                fullWidth
                                size='small'
                                name='netYearly'
                                label={t('employees.agreements.field.yearly.net')}
                                type='number'
                                inputProps={{ step: 0.01 }}
                                value={netYearly}
                                onChange={(event) => {
                                    setNetYearly(parseFloat(event.target.value));
                                }}
                                onBlur={() => {
                                    const newNetDaily = parseFloat((netYearly / 360).toFixed(4));
                                    setNetDaily(newNetDaily);
                                    formik.setFieldValue('net_daily', newNetDaily, true);
                                }}
                            />
                        </Grid>
                    )}

                    {(!isUnlimited &&

                        <Grid item xs={isUnlimited ? 4 : 3}>
                            <AppTextField
                                fullWidth
                                size='small'
                                name='netTotalContract'
                                label={t('employees.agreements.field.contractDuration.net')}
                                type='number'
                                inputProps={{ step: 0.01 }}
                                value={netTotalContract}
                                onChange={(event) => {
                                    setNetTotalContract(parseFloat(event.target.value));
                                }}
                                onBlur={() => {
                                    if (isValidDateLocal(formik.values.startedOn) && isValidDateLocal(formik.values.endedOn)) {
                                        const daysDifference = differenceInDays(new Date(formik.values.endedOn ?? new Date()), new Date(formik.values.startedOn ?? new Date())) + 1;
                                        const newNetDaily = parseFloat((netTotalContract / daysDifference).toFixed(4));
                                        setNetDaily(newNetDaily);
                                        formik.setFieldValue('net_daily', newNetDaily, true);
                                    }
                                }}
                            />
                        </Grid>
                    )}

                    {(!isUnlimited &&

                        <Grid item xs={3}>
                            <AppTextField
                                fullWidth
                                size='small'
                                name='downpayment'
                                label={t('employees.agreements.field.downpayment')}
                                type='number'
                                inputProps={{ step: 0.01 }}
                                value={formik.values.downpayment}
                                onChange={(event) => {
                                    formik.setFieldValue("downpayment", event.target.value, true);
                                }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <H4 mb={3} marginLeft={1} marginBottom={1}>{t('room.notes')}</H4>
                        <AppTextAreaAutosize
                            style={{ width: '100%', maxWidth: '100%', minWidth: '100%' }}
                            minRows={2}
                            maxRows={5}
                            name='notes'
                            value={formik.values.notes}
                            onChange={(event) => {
                                formik.setFieldValue("notes", event.target.value, true);
                            }}
                            placeholder={t('employees.agreements.field.notes.placeholder')}
                        />
                    </Grid>

                    {/* Vacation Configuration Section */}
                    {vacationConfig && (
                        <>
                            <Grid item xs={12}>
                                <H4 mb={2}>
                                    {t('employees.agreements.vacation')}
                                </H4>
                            </Grid>
                            
                            {/* Vacation Type Selection and Value Input in same row */}
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('employees.agreements.field.vacationType')}
                                </Typography>
                                <ToggleButtonGroup
                                    value={vacationType}
                                    exclusive
                                    onChange={(event, newType) => {
                                        if (newType !== null) {
                                            setVacationType(newType);
                                            // Preserve user's entered values when switching
                                            if (newType === 'hours') {
                                                setDaysVacation(0);
                                                formik.setFieldValue('daysVacation', 0, true);
                                                // Use last entered hours if available, otherwise use default
                                                const currentHours = lastEnteredHours > 0 ? lastEnteredHours : vacationConfig.defaultHoursVacation;
                                                setHoursVacation(currentHours);
                                                formik.setFieldValue('hoursVacation', currentHours, true);
                                            } else {
                                                setHoursVacation(0);
                                                formik.setFieldValue('hoursVacation', 0, true);
                                                // Use last entered days if available, otherwise use default
                                                const currentDays = lastEnteredDays > 0 ? lastEnteredDays : vacationConfig.defaultDaysVacation;
                                                setDaysVacation(currentDays);
                                                formik.setFieldValue('daysVacation', currentDays, true);
                                            }
                                        }
                                    }}
                                    aria-label="vacation type"
                                    orientation="vertical"
                                    sx={{
                                        width: '100%',
                                        '& .MuiToggleButton-root': {
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            padding: '12px 16px',
                                            textTransform: 'none',
                                            width: '100%',
                                            justifyContent: 'flex-start',
                                            '&.Mui-selected': {
                                                backgroundColor: '#6200ee',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#5000d6',
                                                }
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="days" aria-label="days">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>📅</span>
                                            {t('employees.agreements.field.daysVacation')}
                                        </Box>
                                    </ToggleButton>
                                    <ToggleButton value="hours" aria-label="hours">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>⏰</span>
                                            {t('employees.agreements.field.hoursVacation')}
                                        </Box>
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            
                            {/* Right column: Info box and input field */}
                            <Grid item xs={6}>
                                {/* Heading to match left column height */}
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    {t('employees.agreements.vacation')}
                                </Typography>
                                
                                {/* Info Alert */}
                                <Alert severity='info' sx={{ mb: 2 }}>
                                    {vacationType === 'hours'
                                        ? t('employees.agreements.info.vacationInHours', { hours: vacationConfig.defaultHoursVacation })
                                        : t('employees.agreements.info.vacationInDays', { days: vacationConfig.defaultDaysVacation })
                                    }
                                </Alert>
                                
                                {/* Vacation Value Input - Only show the active type */}
                                {vacationType === 'hours' ? (
                                    <AppTextField
                                        fullWidth
                                        size='small'
                                        name='hoursVacation'
                                        label={t('employees.agreements.field.hoursVacation')}
                                        type='number'
                                        inputProps={{ step: 0.5, min: 0 }}
                                        value={hoursVacation}
                                        onChange={(event) => {
                                            const value = parseFloat(event.target.value) || 0;
                                            setHoursVacation(value);
                                            setLastEnteredHours(value); // Store the last entered value
                                            formik.setFieldValue('hoursVacation', value, true);
                                        }}
                                    />
                                ) : (
                                    <AppTextField
                                        fullWidth
                                        size='small'
                                        name='daysVacation'
                                        label={t('employees.agreements.field.daysVacation')}
                                        type='number'
                                        inputProps={{ step: 0.5, min: 0 }}
                                        value={daysVacation}
                                        onChange={(event) => {
                                            const value = parseFloat(event.target.value) || 0;
                                            setDaysVacation(value);
                                            setLastEnteredDays(value); // Store the last entered value
                                            formik.setFieldValue('daysVacation', value, true);
                                        }}
                                    />
                                )}
                            </Grid>
                        </>
                    )}

                    {/* Weekly Work Hours Section */}
                    <Grid item xs={12}>
                        <H4 mb={2}>
                            {t('employees.agreements.field.weeklyWorkHours')}
                        </H4>
                    </Grid>
                    
                    <Grid item xs={6}>
                        <AppTextField
                            fullWidth
                            size='small'
                            name='weeklyWorkHours'
                            label={t('employees.agreements.field.weeklyWorkHours')}
                            type='number'
                            inputProps={{ step: 0.5, min: 0, max: 168 }}
                            value={formik.values.weeklyWorkHours ?? weeklyWorkHours}
                            onChange={(event) => {
                                const value = parseFloat(event.target.value) || 0;
                                setWeeklyWorkHours(value);
                                formik.setFieldValue('weeklyWorkHours', value, true);
                            }}
                            helperText={t('employees.agreements.info.weeklyWorkHours')}
                        />
                    </Grid>
                    
                    <Grid item xs={6}>
                        <Alert
                            severity='info'
                            sx={{
                                mt: 0.5,
                                '& .MuiAlert-message': {
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }
                            }}
                        >
                            {t('employees.agreements.info.weeklyWorkHoursDescription')}
                        </Alert>
                    </Grid>

                    {/* Costo Orario Aziendale Section */}
                    {canViewCost && (
                        <>
                            <Grid item xs={12}>
                                <H4 mb={2}>
                                    {t('agreements.costPerHour.label')}
                                </H4>
                            </Grid>
                            <Grid item xs={6}>
                                {canEditCost ? (
                                    <AppTextField
                                        fullWidth
                                        size='small'
                                        name='costPerHour'
                                        label={t('agreements.costPerHour.label')}
                                        type='number'
                                        inputProps={{ step: 0.01, min: 0, max: 999 }}
                                        placeholder='es. 16.50'
                                        value={costPerHour}
                                        onChange={(event) => {
                                            const raw = event.target.value;
                                            if (raw === '') {
                                                setCostPerHour('');
                                            } else {
                                                const v = parseFloat(raw);
                                                if (!isNaN(v) && v >= 0 && v <= 999) setCostPerHour(v);
                                            }
                                        }}
                                        error={typeof costPerHour === 'number' && (costPerHour < 0 || costPerHour > 999)}
                                        helperText={(typeof costPerHour === 'number' && (costPerHour < 0 || costPerHour > 999))
                                            ? t('agreements.costPerHour.outOfRange')
                                            : undefined}
                                    />
                                ) : (
                                    // MANAGER: masked read-only display
                                    <AppTextField
                                        fullWidth
                                        size='small'
                                        label={t('agreements.costPerHour.label')}
                                        value={costPerHour !== '' ? '•••••' : ''}
                                        InputProps={{ readOnly: true }}
                                        helperText={t('agreements.costPerHour.masked')}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Alert
                                    severity='info'
                                    sx={{
                                        mt: 0.5,
                                        '& .MuiAlert-message': {
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                        },
                                    }}
                                >
                                    {/* Hardcoded IT — payroll terminology must not be translated. */}
                                    Costo orario fully-loaded del dipendente per il datore di lavoro: lordo orario + contributi INPS/INAIL + TFR + benefit. NON il lordo orario in busta paga (RAL/h). NON il netto in busta. Esempio: lordo orario €11 → costo orario aziendale ≈ €15-17. Lascia vuoto se non vuoi tracciare il costo del lavoro per questo dipendente.
                                </Alert>
                            </Grid>
                        </>
                    )}

                    {/* 730 Balance Section */}
                    <Grid item xs={12}>
                        <H4 mb={2}>
                            {t('employees.agreements.field.balance730')}
                        </H4>
                    </Grid>
                    
                    <Grid item xs={6}>
                        <AppTextField
                            fullWidth
                            size='small'
                            name='balance730'
                            label={t('employees.agreements.field.balance730')}
                            type='number'
                            inputProps={{ step: 0.01 }}
                            value={balance730Input}
                            onChange={(event) => {
                                const value = event.target.value;
                                setBalance730Input(value);
                                
                                // Update the numeric state for calculations
                                if (value === '' || value === '-') {
                                    setBalance730(0);
                                } else {
                                    const parsed = parseFloat(value);
                                    if (!isNaN(parsed)) {
                                        setBalance730(parsed);
                                    }
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: balance730 > 0 ? 'success.main' : balance730 < 0 ? 'error.main' : 'grey.300',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: balance730 > 0 ? 'success.dark' : balance730 < 0 ? 'error.dark' : 'grey.400',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: balance730 > 0 ? 'success.main' : balance730 < 0 ? 'error.main' : 'primary.main',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: balance730 > 0 ? 'success.main' : balance730 < 0 ? 'error.main' : 'text.primary',
                                },
                                '& .MuiOutlinedInput-input': {
                                    color: balance730 > 0 ? 'success.main' : balance730 < 0 ? 'error.main' : 'text.primary',
                                    fontWeight: balance730 !== 0 ? 600 : 'normal',
                                }
                            }}
                        />
                    </Grid>
                    
                    <Grid item xs={6}>
                        <H4 mb={1}>{t('employees.agreements.field.balance730Date')}</H4>
                        <CalendarInput
                            format='dd.MM.yyyy'
                            value={balance730Date}
                            onChange={(newValue) => {
                                if (newValue) {
                                    if (!(newValue instanceof Date) || isNaN(newValue.getTime())) {
                                        setBalance730Date(newValue);
                                        return;
                                    }
                                    const convertedNewValue = new Date(Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate()));
                                    setBalance730Date(convertedNewValue);
                                } else {
                                    setBalance730Date(null);
                                }
                            }}
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Alert 
                            severity={balance730 > 0 ? 'success' : balance730 < 0 ? 'error' : 'info'}
                            sx={{
                                '& .MuiAlert-message': {
                                    color: balance730 > 0 ? 'success.dark' : balance730 < 0 ? 'error.dark' : 'text.primary',
                                    fontWeight: balance730 !== 0 ? 500 : 'normal',
                                }
                            }}
                        >
                            {t('employees.agreements.info.balance730')}
                        </Alert>
                    </Grid>

                    {(!isUnlimited &&
                        <>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('employees.agreements.totalDays')}
                                </Typography>
                                <Typography variant="h5">
                                    {
                                        totalDays()
                                    }
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t("employees.agreements.field.totalDaysOff")}
                                </Typography>
                                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                    <Typography variant="h5" component="span">
                                        {integerPart}
                                    </Typography>
                                    <Typography variant="subtitle1" component="span" style={{ marginLeft: '4px' }}>
                                        {fractionDisplay(fractionPart)}
                                    </Typography>
                                </div>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={6} alignContent={'center'}>

                        <FormControlLabel
                            sx={{ minWidth: '0px' }}
                            label={<Typography noWrap textOverflow={'ellipsis'} overflow={'hidden'}>{t('employees.agreements.createAutomatically')}</Typography>}
                            labelPlacement='end'
                            control=
                            {<Switch
                                checked={formik.values.createDaysAutomatically}
                                onChange={(e) => {
                                    // if changing from create days automatically to manually set freeDays empty
                                    if (formik.values.createDaysAutomatically) {
                                        formik.setFieldValue('freeDays', []);
                                        formik.setFieldValue('daysOff', 0);
                                    }
                                    // if changing from manually to automatically set days off number to freeDays lenght
                                    else {
                                        formik.setFieldValue('daysOff', formik.values.freeDays.length);
                                    }

                                    formik.setFieldValue('createDaysAutomatically', !formik.values.createDaysAutomatically);

                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />}
                        />
                    </Grid>

                    <Grid item xs={6} alignContent={'center'}>
                        <TextField
                            variant='standard'
                            helperText={
                                (formik.errors.daysOff) ?
                                    t('value.notValid')
                                    : undefined
                            }
                            error={!!formik.errors.daysOff}
                            fullWidth
                            label={t('employees.report.contract.daysOff')}
                            value={formik.values.daysOff}
                            onChange={(e) => {
                                formik.setFieldValue('daysOff', e.target.value);
                            }}
                            type='number'
                            disabled={formik.values.createDaysAutomatically}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Alert severity='warning'>
                            {t('employees.agreements.info.daysOff')}
                        </Alert>
                    </Grid>

                    {/* Free Days per Week Field */}
                    <Grid item xs={6}>
                        <DaysOfWeekPicker
                            disabled={!formik.values.createDaysAutomatically}
                            selectedDays={formik.values.freeDays as unknown as DaysOfTheWeek[]}
                            onChange={(event) => {
                                formik.setFieldValue("freeDays", event, true);
                                formik.setFieldValue('daysOff', event.length);
                            }}
                        />
                    </Grid>


                </Grid>

                <FlexBox justifyContent='flex-end' gap={2} marginTop={4}>
                    <Button fullWidth size='small' variant='outlined' onClick={() => {
                        handleClose();
                    }}>
                        {t('common.forms.button.cancel')}
                    </Button>
                    <Button fullWidth size='small' type='submit' onClick={() => { console.log(formik.values) }} variant='contained'>
                        {t('common.forms.button.save')}
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal >
    );
};

export default AddAgreememtModal;
